import { useMutation } from '@apollo/client';
import React from 'react';
import type { OptionProps } from 'react-select';
import { components } from 'react-select';
import Form, { Field, Submit } from '../../components/Form';
import Modal, { ModalTitle } from '../../components/Modal';
import { ExportFormat, ExportPrivacy } from '../../graphql/generated/graphql';
import UIActions from '../../ui/UIActions';
import styles from './MassExportModal.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';

const EXPORT_ALL_MUTATION = gql(`
  mutation exportAll($format: ExportFormat!, $privacy: ExportPrivacy!) {
    exportAll(format: $format, privacy: $privacy) {
      url
    }
  }
`);

type MassExportSelectOptionType = {
  label: string;
  value: ExportPrivacy;
  description: string;
};

interface MassExportSelectValueProps {
  data: MassExportSelectOptionType;
}

type MassExportSelectOptionProps = Omit<
  OptionProps<MassExportSelectOptionType, true>,
  'data'
> &
  MassExportSelectValueProps;

const MassExportSelectOption = (props: MassExportSelectOptionProps) => (
  <components.Option {...props}>
    <div className={styles.option}>
      <strong>{props.label}</strong>
      <span>{props.data.description}</span>
    </div>
  </components.Option>
);

interface MassExportModalProps {
  close: () => void;
}

type Values = {
  format: ExportFormat;
  privacy: ExportPrivacy;
};

const MassExportModal = ({ close }: MassExportModalProps) => {
  const dispatch = useAppDispatch();

  const [exportAll] = useMutation(EXPORT_ALL_MUTATION);

  const handleSubmit = async (values: Values) => {
    try {
      await exportAll({ variables: values });
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message:
            'Your export is being processed and you will receive an email with a download link once complete. This may take several minutes.',
        }),
      );
      close();
    } catch (err) {}
  };

  return (
    <Modal width={370}>
      <ModalTitle close={close} subtitle="Pick the type and scope to export">
        Export published posts
      </ModalTitle>
      <Form<Values>
        initialValues={{
          format: ExportFormat.Markdown,
          privacy: ExportPrivacy.Open,
        }}
        onSubmit={handleSubmit}
      >
        <Field
          label="File type"
          type="select"
          options={[
            { label: 'Markdown (.md)', value: ExportFormat.Markdown },
            { label: 'Word (.docx)', value: ExportFormat.Docx },
            // { label: 'PDF', value: 'pdf' },
          ]}
          name="format"
        />
        <Field
          label="Topics"
          type="select"
          options={[
            {
              label: 'Open and Public',
              description: 'Posts viewable by any standard user',
              value: ExportPrivacy.Open,
            },
            {
              label: 'Open, Public, and Private',
              description: 'Includes Private topics you are not a member of',
              value: ExportPrivacy.Private,
            },
            {
              label: 'All topics including Secret',
              description:
                'Does not include Secret topics that you do not have access to',
              value: ExportPrivacy.Secret,
            },
          ]}
          name="privacy"
          components={{ Option: MassExportSelectOption }}
        />
        <div className={styles.submitWrapper}>
          <Submit>Download</Submit>
        </div>
      </Form>
    </Modal>
  );
};

export default MassExportModal;
